import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../../lib/adminAPI';
import { useToast } from '../../hooks/use-toast';
import { Bitcoin, Building2, CreditCard, Landmark, Plus, Trash2, Edit3, Check, X as XIcon, Power, PowerOff, Wallet } from 'lucide-react';

const TYPE_META = {
  crypto: { icon: Bitcoin,   label: 'Crypto',       tone: 'text-amber-400',  bg: 'bg-amber-400/10 border-amber-400/30' },
  bank:   { icon: Building2, label: 'Bank Account', tone: 'text-sky-400',    bg: 'bg-sky-400/10 border-sky-400/30' },
  card:   { icon: CreditCard,label: 'Card',         tone: 'text-purple-400', bg: 'bg-purple-400/10 border-purple-400/30' },
  wire:   { icon: Landmark,  label: 'Wire Transfer',tone: 'text-[#00e676]',  bg: 'bg-[#00e676]/10 border-[#00e676]/30' }
};

const EMPTY = {
  type: 'crypto',
  label: '',
  active: true,
  instructions: '',
  min_amount: '',
  network: '',
  address: '',
  memo_tag: '',
  bank_name: '',
  account_holder: '',
  account_number: '',
  iban: '',
  routing_number: '',
  swift_code: '',
  beneficiary_address: '',
  correspondent_bank: '',
  reference: '',
  processor_url: '',
  processor_name: ''
};

const Field = ({ label, value, onChange, placeholder, testid, type = 'text' }) => (
  <div>
    <label className="text-[10px] text-gray-400 uppercase tracking-wider">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded-lg px-3 py-2 text-sm text-white mono focus:outline-none focus:border-[#00e676]"
    />
  </div>
);

const TextArea = ({ label, value, onChange, placeholder, testid }) => (
  <div className="col-span-2">
    <label className="text-[10px] text-gray-400 uppercase tracking-wider">{label}</label>
    <textarea
      data-testid={testid}
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={2}
      className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00e676]"
    />
  </div>
);

const AccountForm = ({ initial, onCancel, onSave, saving }) => {
  const [f, setF] = useState({ ...EMPTY, ...(initial || {}) });
  const upd = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const M = TYPE_META[f.type];

  return (
    <div className="p-5 rounded-xl border border-[#00e676]/30 bg-[#0a1a13] space-y-4" data-testid="deposit-account-form">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          {initial ? <><Edit3 size={14}/> Edit deposit account</> : <><Plus size={14}/> New deposit account</>}
        </h3>
        <button onClick={onCancel} className="text-gray-400 hover:text-white" data-testid="close-form-btn"><XIcon size={16}/></button>
      </div>

      {/* Type selector */}
      <div>
        <label className="text-[10px] text-gray-400 uppercase tracking-wider">Payment Type</label>
        <div className="grid grid-cols-4 gap-2 mt-1">
          {Object.entries(TYPE_META).map(([k, meta]) => {
            const active = f.type === k;
            const Ico = meta.icon;
            return (
              <button
                key={k}
                type="button"
                disabled={!!initial}
                onClick={() => upd('type', k)}
                data-testid={`type-${k}-btn`}
                className={`flex flex-col items-center gap-1 py-3 rounded-lg border text-xs font-semibold transition ${active ? `${meta.bg} ${meta.tone}` : 'border-[#1a2823] text-gray-400 hover:border-[#00e676]/40'} ${initial ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <Ico size={16}/> {meta.label}
              </button>
            );
          })}
        </div>
        {initial && <div className="mt-1 text-[10px] text-gray-500">Type cannot be changed after creation.</div>}
      </div>

      {/* Common fields */}
      <div className="grid grid-cols-2 gap-3">
        <Field testid="label-input" label="Label (shown to traders)" value={f.label} onChange={v => upd('label', v)} placeholder="e.g. BTC · Primary vault"/>
        <Field testid="min-amount-input" label="Minimum Amount (USD)" value={f.min_amount} onChange={v => upd('min_amount', v)} placeholder="20" type="number"/>
      </div>

      {/* Type-specific */}
      {f.type === 'crypto' && (
        <div className="grid grid-cols-2 gap-3">
          <Field testid="network-input" label="Network" value={f.network} onChange={v => upd('network', v)} placeholder="BTC / ETH / USDT-TRC20"/>
          <Field testid="address-input" label="Wallet Address" value={f.address} onChange={v => upd('address', v)} placeholder="bc1q…"/>
          <Field testid="memo-input" label="Memo / Tag (optional)" value={f.memo_tag} onChange={v => upd('memo_tag', v)} placeholder="Only for XRP/XLM/BNB"/>
        </div>
      )}

      {f.type === 'bank' && (
        <div className="grid grid-cols-2 gap-3">
          <Field testid="bank-name-input" label="Bank Name" value={f.bank_name} onChange={v => upd('bank_name', v)} placeholder="Chase Bank"/>
          <Field testid="bank-holder-input" label="Account Holder" value={f.account_holder} onChange={v => upd('account_holder', v)} placeholder="Nexus FX Pro Ltd."/>
          <Field testid="bank-account-input" label="Account Number" value={f.account_number} onChange={v => upd('account_number', v)} placeholder="1234567890"/>
          <Field testid="bank-routing-input" label="Routing Number / IFSC" value={f.routing_number} onChange={v => upd('routing_number', v)} placeholder="021000021"/>
          <Field testid="bank-swift-input" label="SWIFT / BIC" value={f.swift_code} onChange={v => upd('swift_code', v)} placeholder="CHASUS33"/>
          <Field testid="bank-iban-input" label="IBAN (optional)" value={f.iban} onChange={v => upd('iban', v)} placeholder="US33 XXXX…"/>
        </div>
      )}

      {f.type === 'wire' && (
        <div className="grid grid-cols-2 gap-3">
          <Field testid="wire-bank-input" label="Beneficiary Bank" value={f.bank_name} onChange={v => upd('bank_name', v)} placeholder="HSBC London"/>
          <Field testid="wire-holder-input" label="Beneficiary Name" value={f.account_holder} onChange={v => upd('account_holder', v)} placeholder="Nexus FX Pro Ltd."/>
          <Field testid="wire-iban-input" label="IBAN" value={f.iban} onChange={v => upd('iban', v)} placeholder="GB33 XXXX…"/>
          <Field testid="wire-account-input" label="Account Number" value={f.account_number} onChange={v => upd('account_number', v)} placeholder="00987654"/>
          <Field testid="wire-swift-input" label="SWIFT / BIC" value={f.swift_code} onChange={v => upd('swift_code', v)} placeholder="HBUKGB4B"/>
          <Field testid="wire-corr-input" label="Correspondent Bank (optional)" value={f.correspondent_bank} onChange={v => upd('correspondent_bank', v)} placeholder="Citibank NY · CITIUS33"/>
          <Field testid="wire-benef-address-input" label="Beneficiary Address" value={f.beneficiary_address} onChange={v => upd('beneficiary_address', v)} placeholder="Company registered address"/>
          <Field testid="wire-reference-input" label="Reference / Memo" value={f.reference} onChange={v => upd('reference', v)} placeholder="Trader ID or purpose of payment"/>
        </div>
      )}

      {f.type === 'card' && (
        <div className="grid grid-cols-2 gap-3">
          <Field testid="card-processor-input" label="Processor Name" value={f.processor_name} onChange={v => upd('processor_name', v)} placeholder="Stripe / MoonPay / etc."/>
          <Field testid="card-url-input" label="Hosted Checkout URL" value={f.processor_url} onChange={v => upd('processor_url', v)} placeholder="https://…"/>
        </div>
      )}

      <TextArea testid="instructions-input" label="Extra Instructions (optional)" value={f.instructions} onChange={v => upd('instructions', v)} placeholder="e.g. Include your user ID in the reference. Confirmations after 1 network block."/>

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={!!f.active} onChange={e => upd('active', e.target.checked)} data-testid="active-checkbox" className="w-4 h-4 accent-[#00e676]"/>
        <span className="text-gray-300">Visible to traders (active)</span>
      </label>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#141f1b]">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-[#1a2823] text-gray-400 text-sm hover:text-white">Cancel</button>
        <button
          onClick={() => {
            const payload = { ...f };
            if (payload.min_amount === '' || payload.min_amount === null) delete payload.min_amount;
            else payload.min_amount = parseFloat(payload.min_amount);
            onSave(payload);
          }}
          disabled={saving || !f.label}
          data-testid="save-deposit-account-btn"
          className="btn-green px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
        >
          {saving ? 'Saving…' : (initial ? 'Save changes' : 'Create account')}
        </button>
      </div>
    </div>
  );
};

const AccountRow = ({ acc, onEdit, onToggle, onDelete }) => {
  const meta = TYPE_META[acc.type] || TYPE_META.crypto;
  const Ico = meta.icon;
  const detail = (() => {
    if (acc.type === 'crypto') return `${acc.network || '—'} · ${acc.address || '—'}`;
    if (acc.type === 'bank')   return `${acc.bank_name || '—'} · ${acc.account_number || acc.iban || '—'}`;
    if (acc.type === 'wire')   return `${acc.bank_name || '—'} · SWIFT ${acc.swift_code || '—'}`;
    if (acc.type === 'card')   return `${acc.processor_name || 'Card processor'} · ${acc.processor_url || ''}`;
    return '';
  })();
  return (
    <tr className="border-t border-[#141f1b] hover:bg-[#0f1a17]" data-testid={`deposit-row-${acc.id}`}>
      <td className="px-4 py-3">
        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-bold border ${meta.bg} ${meta.tone}`}>
          <Ico size={11}/> {meta.label.toUpperCase()}
        </div>
      </td>
      <td className="text-white font-semibold">{acc.label}</td>
      <td className="text-gray-400 text-xs mono max-w-[420px] truncate">{detail}</td>
      <td>
        {acc.active ? (
          <span className="inline-flex items-center gap-1 text-[10px] text-[#00e676] font-bold"><Power size={10}/> ACTIVE</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] text-gray-500 font-bold"><PowerOff size={10}/> HIDDEN</span>
        )}
      </td>
      <td className="text-right pr-4">
        <div className="inline-flex gap-1">
          <button onClick={() => onToggle(acc)} data-testid={`toggle-${acc.id}-btn`} className="p-1.5 rounded border border-[#1a2823] text-gray-400 hover:text-[#00e676] hover:border-[#00e676]/40" title={acc.active ? 'Hide from traders' : 'Show to traders'}>
            {acc.active ? <PowerOff size={12}/> : <Power size={12}/>}
          </button>
          <button onClick={() => onEdit(acc)} data-testid={`edit-${acc.id}-btn`} className="p-1.5 rounded border border-[#1a2823] text-gray-400 hover:text-white">
            <Edit3 size={12}/>
          </button>
          <button onClick={() => onDelete(acc)} data-testid={`delete-${acc.id}-btn`} className="p-1.5 rounded border border-red-500/30 text-red-400 hover:bg-red-500/10">
            <Trash2 size={12}/>
          </button>
        </div>
      </td>
    </tr>
  );
};

const AdminDepositAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try { setAccounts(await adminAPI.depositAccounts()); }
    catch (e) { toast({ title: 'Failed to load', description: e?.response?.data?.detail || 'Try again' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === 'all') return accounts;
    return accounts.filter(a => a.type === filter);
  }, [accounts, filter]);

  const openCreate = () => { setEditing(null); setShowForm(true); };
  const openEdit = (acc) => { setEditing(acc); setShowForm(true); };

  const save = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        const { type, ...patch } = payload; // type can't change
        await adminAPI.updateDepositAccount(editing.id, patch);
        toast({ title: 'Updated', description: `${payload.label} saved.` });
      } else {
        await adminAPI.createDepositAccount(payload);
        toast({ title: 'Created', description: `${payload.label} is ${payload.active ? 'live' : 'hidden'}.` });
      }
      setShowForm(false);
      setEditing(null);
      await load();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again' });
    } finally { setSaving(false); }
  };

  const toggle = async (acc) => {
    try {
      await adminAPI.updateDepositAccount(acc.id, { active: !acc.active });
      await load();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again' }); }
  };

  const remove = async (acc) => {
    if (!window.confirm(`Delete "${acc.label}"? This cannot be undone.`)) return;
    try {
      await adminAPI.deleteDepositAccount(acc.id);
      toast({ title: 'Deleted', description: `${acc.label} removed.` });
      await load();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again' }); }
  };

  return (
    <div className="space-y-6" data-testid="admin-deposit-accounts-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Wallet size={22} className="text-[#00e676]"/> Deposit Methods
          </h1>
          <p className="text-sm text-gray-400 mt-1">Manage the crypto addresses, bank details, wire instructions, and card processors that traders see on their Deposit page.</p>
        </div>
        <button onClick={openCreate} data-testid="new-deposit-account-btn" className="btn-green px-5 py-2.5 rounded-lg text-sm font-semibold inline-flex items-center gap-2">
          <Plus size={14}/> New Deposit Method
        </button>
      </div>

      {/* Type filter chips */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilter('all')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${filter === 'all' ? 'bg-[#00e676] text-black border-[#00e676]' : 'border-[#1a2823] text-gray-400 hover:text-white'}`}>All ({accounts.length})</button>
        {Object.entries(TYPE_META).map(([k, m]) => {
          const count = accounts.filter(a => a.type === k).length;
          const Ico = m.icon;
          return (
            <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}-btn`} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border inline-flex items-center gap-1.5 ${filter === k ? `${m.bg} ${m.tone}` : 'border-[#1a2823] text-gray-400 hover:text-white'}`}>
              <Ico size={11}/> {m.label} ({count})
            </button>
          );
        })}
      </div>

      {showForm && (
        <AccountForm
          initial={editing}
          saving={saving}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSave={save}
        />
      )}

      <div className="rounded-xl border border-[#1a2823] bg-[#0a1210] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#141f1b] flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">Configured methods ({filtered.length})</h3>
          <span className="text-[10px] text-gray-500 uppercase tracking-wider">Live to traders</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-2">Type</th>
                <th>Label</th>
                <th>Details</th>
                <th>Status</th>
                <th className="text-right pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="5" className="py-10 text-center text-sm text-[#00e676]">Loading…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan="5" className="py-14 text-center text-sm text-gray-500" data-testid="deposit-empty-state">
                  No deposit methods yet. Click "New Deposit Method" to add one.
                </td></tr>
              )}
              {!loading && filtered.map(a => (
                <AccountRow key={a.id} acc={a} onEdit={openEdit} onToggle={toggle} onDelete={remove}/>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Only <span className="text-[#00e676] font-semibold">ACTIVE</span> methods appear to traders. Hidden ones are saved but not shown — useful for rotating vault addresses or seasonal payment providers.
      </p>
    </div>
  );
};

export default AdminDepositAccounts;
